/**
 * Saved-itinerary list display state, section buckets, and persistence handlers.
 *
 * @remarks Colocated with `itineraryList/`. Intentionally separate from
 * {@link useItineraryListDataFlow} so AbortController race logic stays isolated.
 */
import { useCallback, useMemo, useState } from "react";
import { SEARCH_BAR } from "../../../config/global.configuration";
import { usePageVisibility } from "../../../hooks/usePageVisibility.hook";
import { useReducedMotion } from "../../../hooks/useReducedMotion.hook";
import { SavedItinerariesApi } from "../../../services/SavedItinerariesApi";
import {
    authSlice,
    purchasedItinerariesSlice,
    savedItinerariesSlice,
    type SavedItineraryEntry,
    usePersistedSlice,
} from "../../../store/persistence";
import { persistentSliceStore } from "../../../store/persistence/runtime/PersistentSliceStore";
import {
    clearLocalOnlyFlag,
    markEntriesLocalOnly,
    toSavedItineraryUpsertDTO,
} from "../../../tools/savedItinerariesSync.tools";
import {
    isItinerarySaved,
    removeSavedItinerary,
    toggleSavedItinerary,
} from "../../../tools/savedItinerary.tools";
import {
    prepareSavedItinerariesForDisplay,
    prepareSavedItineraryEntriesForDisplay,
    resolveGeocodeItemDisplayLabel,
    resolveSavedItineraryDestinationLabel,
    resolveSavedItineraryOriginLabel,
    resolveSavedItineraryTemporalSection,
    type SavedListEntry,
} from "../../../tools/savedItineraryDisplay.tools";
import { type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { type ContainerProps } from "../../../types/ItineraryPlanner";
import { isUiMotionEnabled } from "../../shared/motion/uiMotion.tools";
import { useSavedItineraryRealtimeEnrich } from "./useSavedItineraryRealtimeEnrich.hook";

/**
 * Replaces the entry itinerary with the live overlay DTO when present.
 *
 * @param entry Persisted saved entry.
 * @param overlayById Ephemeral realtime overlay map.
 * @returns Entry unchanged or with live {@link ItineraryDTO}.
 */
const applyRealtimeOverlayToSavedEntry = (
    entry: SavedListEntry,
    overlayById: Readonly<Record<string, ItineraryDTO>>,
): SavedListEntry => {
    const liveItinerary = overlayById[entry.itinerary.id];
    if ( liveItinerary === undefined ) {
        return entry;
    }

    return {
        ...entry,
        "itinerary": liveItinerary,
    };
};

/** Parameters for {@link useSavedItineraryListState}. */
export interface UseSavedItineraryListStateParams {
    "itineraryListContentMode":    ContainerProps["itineraryListContentMode"];
    "setItineraryListContentMode": ContainerProps["setItineraryListContentMode"];
    "activeSavedFilterTab":        ContainerProps["activeSavedFilterTab"];
    "selectedItinerary":           ContainerProps["selectedItinerary"];
    "setSelectedItinerary":        ContainerProps["setSelectedItinerary"];
    "itineraries":                 ContainerProps["itineraries"];
    "departureSelectedAddress":    ContainerProps["departureSelectedAddress"];
    "selectedAddress":             ContainerProps["selectedAddress"];
    "selectedInputs":              ContainerProps["selectedInputs"];
    "setVisible":                  ContainerProps["setVisible"];
    "onCloseSearchResults":        ContainerProps["onCloseSearchResults"];

    /** Shows a danger toast when save toggle fails. */
    "presentErrorToast": ( message: string ) => void;
}

/** Result of {@link useSavedItineraryListState}. */
export interface UseSavedItineraryListStateResult {
    "isSavedListMode":                    boolean;
    "showItineraryDetailsPrice":          boolean;
    "isSelectedItinerarySaved":           boolean;
    "displayedItineraries":               ItineraryDTO[];
    "pastDisplayedSavedEntries":          SavedListEntry[];
    "currentDisplayedSavedEntries":       SavedListEntry[];
    "upcomingDisplayedSavedEntries":      SavedListEntry[];
    "savedEmptyStateVariant":             "noSaved" | "noUpcoming" | "filteredEmpty";
    "shouldRenderSavedListShell":         boolean;
    "itineraryDetailsDepartureAddress":   ContainerProps["departureSelectedAddress"] | { "result": string };
    "itineraryDetailsDestinationAddress": ContainerProps["selectedAddress"] | { "result": string };

    /**
     * Ephemeral realtime overlay (id → live DTO). Use to merge into the open
     * details view without writing IndexedDB.
     */
    "realtimeOverlayById": Readonly<Record<string, ItineraryDTO>>;

    "handleSavedItineraryToggle":  () => void;
    "handleRemoveSavedItinerary":  ( itineraryId: string ) => void;
    "handleCloseList":             () => void;
    "handleSavedListExitComplete": () => void;
}

/**
 * Derives saved-list display buckets and persistence handlers for the itinerary panel.
 *
 * @param params Content mode, itineraries, address labels, and toast bridge.
 * @returns Saved-list view model and handlers for panel views.
 */
export const useSavedItineraryListState = ( params: UseSavedItineraryListStateParams ): UseSavedItineraryListStateResult => {
    const {
        itineraryListContentMode,
        setItineraryListContentMode,
        activeSavedFilterTab,
        selectedItinerary,
        setSelectedItinerary,
        itineraries,
        departureSelectedAddress,
        selectedAddress,
        selectedInputs,
        setVisible,
        onCloseSearchResults,
        presentErrorToast,
    } = params;

    const reducedMotion = useReducedMotion();
    const [ savedItineraries, setSavedItineraries ] = usePersistedSlice( savedItinerariesSlice );
    const [ purchasedItineraries ] = usePersistedSlice( purchasedItinerariesSlice );
    const [ keepSavedListPresence, setKeepSavedListPresence ] = useState( false );

    const isSavedListMode = itineraryListContentMode === "saved";

    /**
     * Recompute past / current / upcoming buckets when the user returns to the
     * app/tab, when opening the saved list, and once per minute while it stays open.
     */
    const { nowMs } = usePageVisibility( {
        "enabled":        isSavedListMode,
        "trackNow":       true,
        "tickIntervalMs": SEARCH_BAR.refreshTimeIntervalMs,
    } );
    const showItineraryDetailsPrice = !isSavedListMode;
    const isSelectedItinerarySaved = useMemo( () => {
        return isItinerarySaved( savedItineraries, selectedItinerary?.id );
    }, [ savedItineraries, selectedItinerary?.id ] );

    /**
     * Entries feeding the active saved-list tab: favorites on « Favoris », purchased
     * itineraries on « Billets », both concatenated on « Tous ».
     */
    const tabSourceEntries = useMemo( (): SavedListEntry[] => {
        if ( activeSavedFilterTab === "itineraries" ) {
            return savedItineraries;
        }
        if ( activeSavedFilterTab === "tickets" ) {
            return purchasedItineraries;
        }

        return [ ...savedItineraries, ...purchasedItineraries ];
    }, [
        activeSavedFilterTab,
        purchasedItineraries,
        savedItineraries,
    ] );

    const displayedSavedEntries = useMemo( (): SavedListEntry[] => {
        if ( !isSavedListMode ) {
            return [];
        }

        return prepareSavedItineraryEntriesForDisplay( tabSourceEntries, {
            "showPast": true,
            nowMs,
        } );
    }, [
        isSavedListMode,
        nowMs,
        tabSourceEntries,
    ] );
    const pastDisplayedSavedEntries = useMemo( () => {
        return displayedSavedEntries.filter( ( entry ) => resolveSavedItineraryTemporalSection( entry, nowMs ) === "past" );
    }, [
        displayedSavedEntries,
        nowMs,
    ] );
    const scheduledCurrentDisplayedSavedEntries = useMemo( () => {
        return displayedSavedEntries.filter( ( entry ) => resolveSavedItineraryTemporalSection( entry, nowMs ) === "current" );
    }, [
        displayedSavedEntries,
        nowMs,
    ] );
    const scheduledUpcomingDisplayedSavedEntries = useMemo( () => {
        return displayedSavedEntries.filter( ( entry ) => resolveSavedItineraryTemporalSection( entry, nowMs ) === "upcoming" );
    }, [
        displayedSavedEntries,
        nowMs,
    ] );

    const enrichSourceItineraries = useMemo( () => {
        return [
            ...scheduledCurrentDisplayedSavedEntries.map( ( entry ) => entry.itinerary ),
            ...scheduledUpcomingDisplayedSavedEntries.map( ( entry ) => entry.itinerary ),
        ];
    }, [
        scheduledCurrentDisplayedSavedEntries,
        scheduledUpcomingDisplayedSavedEntries,
    ] );

    const { "overlayById": realtimeOverlayById } = useSavedItineraryRealtimeEnrich( {
        "itineraries": enrichSourceItineraries,
        "enabled":     isSavedListMode,
    } );

    const currentDisplayedSavedEntries = useMemo( () => {
        return scheduledCurrentDisplayedSavedEntries.map( ( entry ) => {
            return applyRealtimeOverlayToSavedEntry( entry, realtimeOverlayById );
        } );
    }, [
        realtimeOverlayById,
        scheduledCurrentDisplayedSavedEntries,
    ] );
    const upcomingDisplayedSavedEntries = useMemo( () => {
        return scheduledUpcomingDisplayedSavedEntries.map( ( entry ) => {
            return applyRealtimeOverlayToSavedEntry( entry, realtimeOverlayById );
        } );
    }, [
        realtimeOverlayById,
        scheduledUpcomingDisplayedSavedEntries,
    ] );
    const displayedItineraries = useMemo( () => {
        if ( !isSavedListMode ) {
            return itineraries;
        }

        return prepareSavedItinerariesForDisplay( tabSourceEntries, {
            "showPast": true,
            nowMs,
        } ).map( ( itinerary ) => {
            return realtimeOverlayById[itinerary.id] ?? itinerary;
        } );
    }, [
        isSavedListMode,
        itineraries,
        nowMs,
        realtimeOverlayById,
        tabSourceEntries,
    ] );
    const selectedSavedEntry = useMemo( () => {
        if ( selectedItinerary?.id == null ) {
            return undefined;
        }

        return savedItineraries.find( ( entry ) => entry.itinerary.id === selectedItinerary.id );
    }, [ savedItineraries, selectedItinerary ] );
    const itineraryDetailsDepartureAddress = useMemo( () => {
        if ( selectedSavedEntry !== undefined ) {
            return { "result": resolveSavedItineraryOriginLabel( selectedSavedEntry ) };
        }

        return departureSelectedAddress;
    }, [ departureSelectedAddress, selectedSavedEntry ] );
    const itineraryDetailsDestinationAddress = useMemo( () => {
        if ( selectedSavedEntry !== undefined ) {
            return { "result": resolveSavedItineraryDestinationLabel( selectedSavedEntry ) };
        }

        return selectedAddress;
    }, [ selectedAddress, selectedSavedEntry ] );

    /**
     * Pushes a newly favorited entry to the authenticated profile.
     *
     * Fire-and-forget: the local store is already updated. On failure the entry
     * is flagged `localOnly` so it is offered for merge at the next login;
     * on success a stale flag is cleared defensively.
     *
     * @param entry Saved itinerary entry just stored locally.
     */
    const pushSavedItineraryUpsert = useCallback( ( entry: SavedItineraryEntry ): void => {
        const currentAccessToken = persistentSliceStore.peek( authSlice ).accessToken;
        if ( currentAccessToken == null || currentAccessToken === "" ) {
            return;
        }

        void SavedItinerariesApi.upsertSavedItineraries( [ toSavedItineraryUpsertDTO( entry ) ] ).
            then( () => {
                setSavedItineraries( ( previousSavedItineraries ) => clearLocalOnlyFlag( previousSavedItineraries, new Set( [ entry.itinerary.id ] ) ) );
            } ).
            catch( ( error: unknown ) => {
                console.error( "[SavedItineraries] Failed to sync new favorite to the profile", error );
                setSavedItineraries( ( previousSavedItineraries ) => markEntriesLocalOnly( previousSavedItineraries, new Set( [ entry.itinerary.id ] ) ) );
            } );
    }, [ setSavedItineraries ] );

    /**
     * Pushes a favorite removal to the authenticated profile.
     *
     * Fire-and-forget: the local store is already updated. On failure the entry
     * may reappear at the next server-authoritative sync (no tombstones) — the
     * error is logged so the inconsistency stays actionable.
     *
     * @param itineraryId Itinerary id just removed locally.
     */
    const pushSavedItineraryDeletion = useCallback( ( itineraryId: string ): void => {
        const currentAccessToken = persistentSliceStore.peek( authSlice ).accessToken;
        if ( currentAccessToken == null || currentAccessToken === "" ) {
            return;
        }

        void SavedItinerariesApi.deleteSavedItinerary( itineraryId ).catch( ( error: unknown ) => {
            console.error( "[SavedItineraries] Failed to delete favorite from the profile — it may reappear at the next sync", error );
        } );
    }, [] );

    /**
     * Persists or removes the currently selected saved itinerary.
     */
    const handleSavedItineraryToggle = useCallback( (): void => {
        if ( selectedItinerary === null ) {
            return;
        }

        const originLabel = resolveGeocodeItemDisplayLabel( departureSelectedAddress ) ??
          resolveGeocodeItemDisplayLabel( selectedInputs.departureAddress );
        const destinationLabel = resolveGeocodeItemDisplayLabel( selectedAddress ) ??
          resolveGeocodeItemDisplayLabel( selectedInputs.arrivalAddress );

        try {
            const { nextSavedItineraries, isSaved } = toggleSavedItinerary( savedItineraries, selectedItinerary, {
                ...originLabel !== undefined ? { originLabel } : {},
                ...destinationLabel !== undefined ? { destinationLabel } : {},
            } );
            setSavedItineraries( nextSavedItineraries );

            if ( isSaved ) {
                const savedEntry = nextSavedItineraries.find( ( entry ) => entry.itinerary.id === selectedItinerary.id );
                if ( savedEntry !== undefined ) {
                    pushSavedItineraryUpsert( savedEntry );
                }
            } else {
                pushSavedItineraryDeletion( selectedItinerary.id );
            }
        } catch( error: unknown ) {
            console.error( "[ItineraryList] Failed to toggle saved itinerary", error );
            presentErrorToast( "Impossible d'enregistrer cet itinéraire" );
        }
    }, [
        departureSelectedAddress,
        presentErrorToast,
        pushSavedItineraryDeletion,
        pushSavedItineraryUpsert,
        savedItineraries,
        selectedAddress,
        selectedInputs.arrivalAddress,
        selectedInputs.departureAddress,
        selectedItinerary,
        setSavedItineraries,
    ] );

    /**
     * Removes one saved itinerary from the persisted list.
     *
     * @param itineraryId Stable itinerary id to remove.
     */
    const handleRemoveSavedItinerary = useCallback( ( itineraryId: string ): void => {
        if (
            displayedSavedEntries.length === 1 &&
            isUiMotionEnabled( reducedMotion )
        ) {
            setKeepSavedListPresence( true );
        }

        setSavedItineraries( ( previousSavedItineraries ) => {
            return removeSavedItinerary( previousSavedItineraries, itineraryId );
        } );
        pushSavedItineraryDeletion( itineraryId );
        setSelectedItinerary( ( previousSelectedItinerary ) => {
            if ( previousSelectedItinerary?.id === itineraryId ) {
                return null;
            }

            return previousSelectedItinerary;
        } );
    }, [
        displayedSavedEntries.length,
        pushSavedItineraryDeletion,
        reducedMotion,
        setSavedItineraries,
        setSelectedItinerary,
    ] );

    /**
     * Closes saved-itinerary mode and hides the itinerary panel.
     */
    const handleCloseSavedList = useCallback( (): void => {
        setKeepSavedListPresence( false );
        setItineraryListContentMode( "search" );
        setSelectedItinerary( null );
        setVisible( false );
    }, [ setSelectedItinerary, setItineraryListContentMode, setVisible ] );

    const handleCloseList = isSavedListMode ? handleCloseSavedList : onCloseSearchResults;

    const savedEmptyStateVariant = useMemo( (): "noSaved" | "noUpcoming" | "filteredEmpty" => {
        if ( savedItineraries.length === 0 && activeSavedFilterTab !== "tickets" ) {
            return "noSaved";
        }

        if (
            pastDisplayedSavedEntries.length > 0 &&
            scheduledCurrentDisplayedSavedEntries.length === 0 &&
            scheduledUpcomingDisplayedSavedEntries.length === 0
        ) {
            return "noUpcoming";
        }

        return "filteredEmpty";
    }, [
        activeSavedFilterTab,
        pastDisplayedSavedEntries.length,
        savedItineraries.length,
        scheduledCurrentDisplayedSavedEntries.length,
        scheduledUpcomingDisplayedSavedEntries.length,
    ] );

    const handleSavedListExitComplete = useCallback( (): void => {
        if ( displayedSavedEntries.length === 0 ) {
            setKeepSavedListPresence( false );
        }
    }, [ displayedSavedEntries.length ] );

    const shouldRenderSavedListShell = displayedSavedEntries.length > 0 || keepSavedListPresence;

    return {
        isSavedListMode,
        showItineraryDetailsPrice,
        isSelectedItinerarySaved,
        displayedItineraries,
        pastDisplayedSavedEntries,
        currentDisplayedSavedEntries,
        upcomingDisplayedSavedEntries,
        savedEmptyStateVariant,
        shouldRenderSavedListShell,
        itineraryDetailsDepartureAddress,
        itineraryDetailsDestinationAddress,
        realtimeOverlayById,
        handleSavedItineraryToggle,
        handleRemoveSavedItinerary,
        handleCloseList,
        handleSavedListExitComplete,
    };
};
